import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useActionLock } from '@breaker/action-lock';
import { Link } from '@tanstack/react-router';
import {
  DndContext,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { PortalSectionType } from '@breaker/shared-types';
import { HIGHLIGHT_PRESETS, type ThemePreset } from '@breaker/shared-schemas';
import { useActiveFleet } from '@/data/fleets';
import {
  siteSenders,
  useAddSection,
  useCreatePage,
  useDeletePage,
  useDeleteSection,
  useDiscardDraft,
  usePublishSite,
  useReorderPages,
  useReorderSections,
  useSiteImageInit,
  useSiteStudio,
  type PreviewFleetInfo,
} from '@/data/site';
import { AutosaveQueue } from '@/lib/site-autosave';
import { contrastWarning } from '@/lib/brand-preview';
import {
  resolvePreviewSections,
  HIGHLIGHT_LABELS,
} from '@/lib/site-preview-resolver';
import {
  DriverAppPreview,
  SitePagePreview,
  TrackingCardPreview,
} from '@/components/site-preview';
import { RichTextEditor, EMPTY_DOC } from '@/components/rich-text-editor';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Spinner } from '@/components/ui/spinner';
import { Field, Input } from '@/components/ui/input';
import { ColorPicker } from '@/components/ui/color-picker';
import { useToast } from '@/components/ui/toast';
import { cn } from '@/lib/cn';

/**
 * "Your website" (Website Studio W4, pages + full library W6/W7, SEO
 * W8) — the editor IS the product: plain forms + keystroke-instant
 * preview, autosave to draft (no save button), one Publish. Pages are
 * chips above the section list; nav derives from visible pages and is
 * never edited separately. The word "CMS" appears nowhere.
 */

interface LocalSection {
  id: string;
  type: PortalSectionType;
  enabled: boolean;
  content: Record<string, unknown>;
}

interface LocalPage {
  id: string;
  slug: string;
  title: string;
  navVisible: boolean;
  isSystem: boolean;
  seoTitle: string | null;
  seoDescription: string | null;
  sections: LocalSection[];
}

const SECTION_TYPES: Array<{
  type: PortalSectionType;
  name: string;
  blurb: string;
}> = [
  { type: 'hero', name: 'Big welcome banner', blurb: 'Headline, a line under it, optional photo.' },
  { type: 'about', name: 'About your company', blurb: 'A paragraph plus an optional photo.' },
  { type: 'highlights', name: 'What you offer', blurb: 'Three to six service badges.' },
  { type: 'gallery', name: 'Photo gallery', blurb: 'Up to eight photos of your work.' },
  { type: 'faq', name: 'Common questions', blurb: 'Questions and answers, up to eight.' },
  { type: 'coverage', name: 'Where you deliver', blurb: 'The areas you cover, as a list.' },
  { type: 'text', name: 'Extra text', blurb: 'Your own paragraphs, with bold and lists.' },
  { type: 'cta', name: 'Book-now banner', blurb: 'A coloured banner with a booking button.' },
  { type: 'contact', name: 'Contact details', blurb: 'Phone, email and address from Settings.' },
];

const DEFAULT_ENVELOPES: Record<string, unknown> = {
  hero: { type: 'hero', content: {} },
  about: { type: 'about', content: {} },
  highlights: {
    type: 'highlights',
    content: {
      items: [
        { kind: 'preset', value: 'same_day' },
        { kind: 'preset', value: 'multi_drop' },
        { kind: 'preset', value: 'nationwide' },
      ],
    },
  },
  gallery: { type: 'gallery', content: { images: [] } },
  faq: { type: 'faq', content: { items: [] } },
  coverage: { type: 'coverage', content: { areas: [] } },
  text: { type: 'text', content: { doc: EMPTY_DOC } },
  cta: { type: 'cta', content: {} },
  contact: { type: 'contact', content: {} },
};

/**
 * The server refuses half-typed entries (empty FAQ answer, blank
 * coverage area, custom highlight with no label yet, <3 highlight
 * items). Keep those rows LOCALLY while typing but strip them from the
 * autosaved payload — returning null skips the save entirely rather
 * than toasting an error at someone mid-sentence.
 */
function sanitizeForSave(
  type: PortalSectionType,
  content: Record<string, unknown>,
): Record<string, unknown> | null {
  switch (type) {
    case 'faq': {
      const items = (content['items'] as Array<{ q: string; a: string }>) ?? [];
      return { items: items.filter((i) => i.q.trim() && i.a.trim()) };
    }
    case 'coverage': {
      const areas = (content['areas'] as string[]) ?? [];
      return { areas: areas.filter((a) => a.trim()) };
    }
    case 'highlights': {
      const items =
        (content['items'] as Array<{ kind: string; value: string }>) ?? [];
      const complete = items.filter((i) => i.kind === 'preset' || i.value.trim());
      if (complete.length < 3) return null;
      return { items: complete };
    }
    default:
      return content;
  }
}

function CountedInput({
  id,
  value,
  max,
  placeholder,
  textarea,
  onChange,
}: {
  id: string;
  value: string;
  max: number;
  placeholder: string;
  textarea?: boolean;
  onChange: (v: string) => void;
}) {
  const near = value.length >= max - Math.max(5, Math.floor(max * 0.1));
  return (
    <div>
      {textarea ? (
        <textarea
          id={id}
          value={value}
          maxLength={max}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          rows={4}
          className="block w-full rounded-md border border-breaker-line-2 bg-white px-3 py-2 text-sm text-breaker-ink placeholder:text-breaker-mute focus:outline-none"
        />
      ) : (
        <Input
          id={id}
          value={value}
          maxLength={max}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
      <p
        className={cn(
          'mt-1 text-right text-2xs',
          near ? 'text-breaker-amber' : 'text-breaker-mute',
        )}
      >
        {value.length}/{max}
      </p>
    </div>
  );
}

export function YourWebsitePage() {
  const { fleet, role } = useActiveFleet();
  const fleetId = fleet?.id ?? null;
  const canPublish = role === 'owner' || role === 'admin';
  const toast = useToast();

  const studio = useSiteStudio(fleetId);
  const createPage = useCreatePage(fleetId);
  const deletePage = useDeletePage(fleetId);
  const reorderPages = useReorderPages(fleetId);
  const addSection = useAddSection(fleetId);
  const deleteSection = useDeleteSection(fleetId);
  const reorderSections = useReorderSections(fleetId);
  const publish = usePublishSite(fleetId);
  const discard = useDiscardDraft(fleetId);
  const imageInit = useSiteImageInit(fleetId);

  // ---- local draft state (the preview's truth) ----
  const [brand, setBrand] = useState<{
    primaryHex: string | null;
    accentHex: string | null;
    logoUrl: string | null;
  }>({ primaryHex: null, accentHex: null, logoUrl: null });
  const [pages, setPages] = useState<LocalPage[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [localDirty, setLocalDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [frame, setFrame] = useState<'phone' | 'desktop'>('phone');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [mobilePreview, setMobilePreview] = useState(false);
  const [newPageTitle, setNewPageTitle] = useState('');
  const [addingPage, setAddingPage] = useState(false);
  const needsSync = useRef(true);

  const queue = useRef<AutosaveQueue | null>(null);
  if (!queue.current) {
    queue.current = new AutosaveQueue({
      onError: () => toast.error("A change didn't save — check your connection."),
      onBusyChange: setSaving,
    });
  }
  const senders = useMemo(() => (fleetId ? siteSenders(fleetId) : null), [fleetId]);

  // Sync local state from the server on load and after structural ops.
  useEffect(() => {
    if (!studio.data || !needsSync.current) return;
    const next = studio.data.draft.pages.map((p) => ({
      id: p.id,
      slug: p.slug,
      title: p.title,
      navVisible: p.navVisible,
      isSystem: p.isSystem,
      seoTitle: p.seoTitle,
      seoDescription: p.seoDescription,
      sections: p.sections.map((s) => ({
        id: s.id,
        type: s.type,
        enabled: s.enabled,
        content: s.content,
      })),
    }));
    setPages(next);
    setSelectedId((cur) =>
      cur && next.some((p) => p.id === cur)
        ? cur
        : ((next.find((p) => p.isSystem) ?? next[0])?.id ?? null),
    );
    setBrand({
      primaryHex:
        studio.data.draft.content?.brandPrimaryHex ?? fleet?.brandPrimaryHex ?? null,
      accentHex:
        studio.data.draft.content?.brandAccentHex ?? fleet?.brandAccentHex ?? null,
      logoUrl: studio.data.draft.content?.logoUrl ?? fleet?.logoUrl ?? null,
    });
    needsSync.current = false;
  }, [studio.data, fleet]);

  const resync = useCallback(() => {
    needsSync.current = true;
    void studio.refetch();
  }, [studio]);

  const selected = pages.find((p) => p.id === selectedId) ?? null;

  // ---- edit plumbing: local update + autosave ----
  const editBrand = useCallback(
    (patch: Partial<typeof brand>) => {
      setBrand((prev) => {
        const next = { ...prev, ...patch };
        if (senders) {
          queue.current!.save(
            'brand',
            {
              brandPrimaryHex: next.primaryHex,
              brandAccentHex: next.accentHex,
              logoUrl: next.logoUrl,
            },
            senders.brand,
          );
        }
        return next;
      });
      setLocalDirty(true);
    },
    [senders],
  );

  /** Page-level fields save as ONE coalesced patch per page — a rename
   *  and a nav toggle in the same window must both land. */
  const editPage = useCallback(
    (
      id: string,
      patch: Partial<
        Pick<LocalPage, 'title' | 'navVisible' | 'seoTitle' | 'seoDescription'>
      >,
    ) => {
      setPages((prev) =>
        prev.map((p) => {
          if (p.id !== id) return p;
          const next = { ...p, ...patch };
          if (senders && next.title.trim().length > 0) {
            queue.current!.save(
              `page:${id}`,
              {
                ...(next.isSystem ? {} : { title: next.title.trim() }),
                navVisible: next.navVisible,
                seoTitle: next.seoTitle?.trim() || null,
                seoDescription: next.seoDescription?.trim() || null,
              },
              senders.page(id),
            );
          }
          return next;
        }),
      );
      setLocalDirty(true);
    },
    [senders],
  );

  const editSection = useCallback(
    (sectionId: string, content: Record<string, unknown>) => {
      let type: PortalSectionType | undefined;
      setPages((prev) =>
        prev.map((p) => ({
          ...p,
          sections: p.sections.map((s) => {
            if (s.id !== sectionId) return s;
            type = s.type;
            return { ...s, content };
          }),
        })),
      );
      if (senders && type) {
        const clean = sanitizeForSave(type, content);
        if (clean) {
          queue.current!.save(
            `section:${sectionId}`,
            { envelope: { type, content: clean } },
            senders.section(sectionId),
          );
        }
      }
      setLocalDirty(true);
    },
    [senders],
  );

  const toggleSection = useCallback(
    (sectionId: string, enabled: boolean) => {
      setPages((prev) =>
        prev.map((p) => ({
          ...p,
          sections: p.sections.map((s) =>
            s.id === sectionId ? { ...s, enabled } : s,
          ),
        })),
      );
      if (senders) {
        queue.current!.save(
          `section-en:${sectionId}`,
          { enabled },
          senders.section(sectionId),
        );
      }
      setLocalDirty(true);
    },
    [senders],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 8 } }),
  );

  const onDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id || !selected) return;
      const pageId = selected.id;
      setPages((prev) =>
        prev.map((p) => {
          if (p.id !== pageId) return p;
          const from = p.sections.findIndex((s) => s.id === active.id);
          const to = p.sections.findIndex((s) => s.id === over.id);
          const next = arrayMove(p.sections, from, to);
          reorderSections.mutate(
            { pageId, orderedIds: next.map((s) => s.id) },
            { onError: () => toast.error("Couldn't reorder — try again.") },
          );
          return { ...p, sections: next };
        }),
      );
      setLocalDirty(true);
    },
    [selected, reorderSections, toast],
  );

  const onAdd = useCallback(
    (type: PortalSectionType) => {
      if (!selected) return Promise.resolve();
      setPickerOpen(false);
      return addSection
        .mutateAsync({ pageId: selected.id, envelope: DEFAULT_ENVELOPES[type] })
        .then(resync)
        .catch(() => toast.error("Couldn't add the section."));
    },
    [selected, addSection, resync, toast],
  );

  const onDelete = useCallback(
    (sectionId: string) => {
      if (!window.confirm('Remove this section? You can add it again later.'))
        return Promise.resolve();
      return deleteSection
        .mutateAsync(sectionId)
        .then(resync)
        .catch(() => toast.error("Couldn't remove the section."));
    },
    [deleteSection, resync, toast],
  );

  // ---- pages rail ops (W6) ----
  // Unit D: ONE lock shared by the Enter-to-add keydown path and the Add
  // button, so the two routes can't fire the create twice.
  const addPageLock = useActionLock();
  const onAddPage = useCallback(() => {
    const title = newPageTitle.trim();
    if (!title) return;
    addPageLock.run(() =>
      createPage
        .mutateAsync(title)
        .then((r) => {
          setNewPageTitle('');
          setAddingPage(false);
          setSelectedId(r.page.id);
          resync();
          setLocalDirty(true);
        })
        .catch((e) =>
          toast.error(e instanceof Error ? e.message : "Couldn't add the page."),
        ),
    );
  }, [newPageTitle, addPageLock.run, createPage, resync, toast]);

  const onDeletePage = useCallback(() => {
    if (!selected || selected.isSystem) return Promise.resolve();
    if (
      !window.confirm(
        `Delete the "${selected.title}" page and everything on it? This can't be undone after you publish.`,
      )
    )
      return Promise.resolve();
    return deletePage
      .mutateAsync(selected.id)
      .then(() => {
        setSelectedId(null);
        resync();
        setLocalDirty(true);
      })
      .catch(() => toast.error("Couldn't delete the page."));
  }, [selected, deletePage, resync, toast]);

  const movePage = useCallback(
    (dir: -1 | 1) => {
      if (!selected || selected.isSystem) return Promise.resolve();
      const idx = pages.findIndex((p) => p.id === selected.id);
      const to = idx + dir;
      if (to < 1 || to >= pages.length) return Promise.resolve(); // slot 0 is Home, pinned
      const next = arrayMove(pages, idx, to);
      setPages(next);
      setLocalDirty(true);
      return reorderPages
        .mutateAsync(next.map((p) => p.id))
        .catch(() => toast.error("Couldn't move the page."));
    },
    [selected, pages, reorderPages, toast],
  );

  const uploadImage = useCallback(
    async (kind: 'hero' | 'about' | 'gallery', file: File): Promise<string | null> => {
      const mimeType = ['image/png', 'image/webp'].includes(file.type)
        ? file.type
        : 'image/jpeg';
      try {
        const presign = await imageInit.mutateAsync({
          kind,
          mimeType,
          sizeBytes: file.size,
        });
        const put = await fetch(presign.uploadUrl, {
          method: 'PUT',
          headers: presign.uploadHeaders,
          body: file,
        });
        if (!put.ok) throw new Error(`upload ${put.status}`);
        return presign.publicUrl;
      } catch {
        toast.error("The photo didn't upload — try a smaller image.");
        return null;
      }
    },
    [imageInit, toast],
  );

  const onPublish = useCallback(async () => {
    await queue.current!.flushAll();
    return publish
      .mutateAsync(undefined)
      .then(() => {
        setLocalDirty(false);
        resync();
        toast.success('Published — your website is live.');
      })
      .catch((e) => toast.error(e instanceof Error ? e.message : 'Publish failed'));
  }, [publish, resync, toast]);

  const onDiscard = useCallback(() => {
    if (!window.confirm('Discard all unpublished changes and go back to the live site?'))
      return Promise.resolve();
    return discard
      .mutateAsync(undefined)
      .then(() => {
        setLocalDirty(false);
        resync();
        toast.success('Draft discarded.');
      })
      .catch(() => toast.error("Couldn't discard the draft."));
  }, [discard, resync, toast]);

  // Bootstrap the draft scaffold for a fresh fleet (copy-on-write Home).
  useEffect(() => {
    if (studio.data && !studio.data.draft.pages.some((p) => p.isSystem) && senders) {
      void senders.brand({}).then(resync);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studio.data?.draft.pages.length, senders]);

  if (!fleetId || studio.isLoading || !studio.data) {
    return (
      <div className="flex justify-center py-24">
        <Spinner size="lg" />
      </div>
    );
  }

  const previewFleet: PreviewFleetInfo = studio.data.previewFleet;
  const dirty = localDirty || studio.data.dirty;
  const warn = contrastWarning(brand.accentHex);
  const previewSections = selected
    ? resolvePreviewSections(previewFleet, selected.isSystem, selected.sections)
    : [];
  const previewNav = pages
    .filter((p) => p.navVisible && !p.isSystem)
    .map((p) => ({ title: p.title, current: p.id === selectedId }));

  return (
    <div className="px-4 sm:px-8 py-8 max-w-[1400px]">
      <header className="mb-6 flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl font-semibold tracking-tight text-breaker-ink">
            Your website
          </h1>
          <p className="text-sm text-breaker-mute mt-1">
            Edit on the left, watch it update on the right. Changes save as you
            type and go live when you press Publish.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {saving ? (
            <span className="text-2xs text-breaker-mute">Saving…</span>
          ) : dirty ? (
            <Badge tone="info">You have unpublished changes</Badge>
          ) : (
            <Badge tone="success">Live</Badge>
          )}
          {dirty ? (
            <Button variant="ghost" size="sm" action={onDiscard}>
              Discard draft
            </Button>
          ) : null}
          <Button
            size="sm"
            action={onPublish}
            loading={publish.isPending}
            disabled={!canPublish || !dirty}
            title={canPublish ? undefined : 'Only the owner or an admin can publish'}
          >
            Publish
          </Button>
        </div>
      </header>

      {/* ---- pages rail (W6) ---- */}
      <div className="mb-6 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          {pages.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => setSelectedId(p.id)}
              className={cn(
                'rounded-full border px-3 py-1.5 text-sm',
                p.id === selectedId
                  ? 'border-breaker-ink bg-breaker-ink text-white'
                  : 'border-breaker-line-2 bg-white text-breaker-ink-2 hover:bg-breaker-ground-2',
                !p.navVisible && !p.isSystem ? 'opacity-60' : '',
              )}
            >
              {p.title}
              {!p.navVisible && !p.isSystem ? ' (hidden)' : ''}
            </button>
          ))}
          {addingPage ? (
            <span className="flex items-center gap-1.5">
              <Input
                autoFocus
                value={newPageTitle}
                maxLength={60}
                placeholder="Page name, e.g. Our services"
                onChange={(e) => setNewPageTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') onAddPage();
                  if (e.key === 'Escape') setAddingPage(false);
                }}
              />
              <Button
                size="sm"
                onClick={onAddPage}
                loading={createPage.isPending || addPageLock.locked}
              >
                Add
              </Button>
            </span>
          ) : (
            <Button variant="ghost" size="sm" onClick={() => setAddingPage(true)}>
              + Add a page
            </Button>
          )}
        </div>
        {selected && !selected.isSystem ? (
          <div className="flex flex-wrap items-center gap-3 rounded-lg border border-breaker-line bg-breaker-ground-2 px-3 py-2">
            <Input
              value={selected.title}
              maxLength={60}
              aria-label="Page name"
              onChange={(e) => editPage(selected.id, { title: e.target.value })}
              className="max-w-[220px]"
            />
            <label className="flex items-center gap-1.5 text-sm text-breaker-ink-2">
              <input
                type="checkbox"
                checked={selected.navVisible}
                onChange={(e) => editPage(selected.id, { navVisible: e.target.checked })}
              />
              Show in menu
            </label>
            <span className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                action={() => movePage(-1)}
                loading={reorderPages.isPending}
                aria-label="Move page left"
              >
                ←
              </Button>
              <Button
                variant="ghost"
                size="sm"
                action={() => movePage(1)}
                loading={reorderPages.isPending}
                aria-label="Move page right"
              >
                →
              </Button>
            </span>
            <span className="flex-1" />
            <span className="text-2xs text-breaker-mute">/{selected.slug}</span>
            <Button variant="ghost" size="sm" action={onDeletePage}>
              Delete page
            </Button>
          </div>
        ) : null}
      </div>

      <div className="mb-4 lg:hidden">
        <Button variant="secondary" size="sm" onClick={() => setMobilePreview((v) => !v)}>
          {mobilePreview ? 'Back to editing' : 'Preview my site'}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
        {/* ---- editor column ---- */}
        <div className={cn('space-y-6', mobilePreview ? 'hidden lg:block' : '')}>
          <section className="surface rounded-lg p-4 space-y-4">
            <h2 className="text-sm font-semibold text-breaker-ink">Your colours</h2>
            <div className="flex flex-wrap gap-2">
              {studio.data.presets.map((preset: ThemePreset) => (
                <button
                  key={preset.key}
                  type="button"
                  title={preset.name}
                  onClick={() =>
                    editBrand({ primaryHex: preset.primaryHex, accentHex: preset.accentHex })
                  }
                  className={cn(
                    'h-9 w-14 rounded-md border overflow-hidden flex',
                    brand.primaryHex === preset.primaryHex &&
                      brand.accentHex === preset.accentHex
                      ? 'border-breaker-ink ring-2 ring-breaker-ink/30'
                      : 'border-breaker-line',
                  )}
                >
                  <span className="flex-1" style={{ background: preset.primaryHex }} />
                  <span className="w-4" style={{ background: preset.accentHex }} />
                </button>
              ))}
            </div>
            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 text-sm text-breaker-ink-2">
                Main colour
                <ColorPicker
                  id="siteMainColour"
                  value={brand.primaryHex ?? '#13293d'}
                  onChange={(hex) => editBrand({ primaryHex: hex })}
                />
              </label>
              <label className="flex items-center gap-2 text-sm text-breaker-ink-2">
                Highlight colour
                <ColorPicker
                  id="siteHighlightColour"
                  value={brand.accentHex ?? '#f2a03d'}
                  onChange={(hex) => editBrand({ accentHex: hex })}
                />
              </label>
            </div>
            {warn ? (
              <p className="text-xs text-breaker-ink bg-breaker-ground-2 border border-breaker-line rounded-md p-2">
                That highlight colour is hard to read on itself, so text on it
                will use plain black or white. It stays accessible — but a
                slightly darker or lighter shade usually looks better.
              </p>
            ) : null}
          </section>

          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-breaker-ink">
                {selected?.isSystem
                  ? 'Home page sections'
                  : `"${selected?.title ?? ''}" sections`}
              </h2>
              <Button variant="secondary" size="sm" onClick={() => setPickerOpen(true)}>
                Add a section
              </Button>
            </div>
            {!selected || selected.sections.length === 0 ? (
              <p className="text-sm text-breaker-mute surface rounded-lg p-4">
                {selected?.isSystem
                  ? 'Your Home page is using our ready-made layout. Add a section to start making it yours — the preview shows exactly what visitors see.'
                  : 'This page is empty. Add a section to put something on it.'}
              </p>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={onDragEnd}
              >
                <SortableContext
                  items={selected.sections.map((s) => s.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-3">
                    {selected.sections.map((section) => (
                      <SectionCard
                        key={section.id}
                        section={section}
                        onEdit={(content) => editSection(section.id, content)}
                        onToggle={(enabled) => toggleSection(section.id, enabled)}
                        onDelete={() => onDelete(section.id)}
                        uploadImage={uploadImage}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}
          </section>

          {/* ---- SEO (W8): plain-English, per page ---- */}
          {selected ? (
            <section className="surface rounded-lg p-4 space-y-3">
              <h2 className="text-sm font-semibold text-breaker-ink">Search engines</h2>
              <p className="text-xs text-breaker-mute">
                This is what Google shows for this page. Leave blank and we
                write a sensible one from your company name.
              </p>
              <Field label="Title in search results" htmlFor="seo-title">
                <CountedInput
                  id="seo-title"
                  value={selected.seoTitle ?? ''}
                  max={70}
                  placeholder={
                    selected.isSystem
                      ? previewFleet.name
                      : `${selected.title} — ${previewFleet.name}`
                  }
                  onChange={(v) => editPage(selected.id, { seoTitle: v || null })}
                />
              </Field>
              <Field label="Line underneath" htmlFor="seo-desc">
                <CountedInput
                  id="seo-desc"
                  textarea
                  value={selected.seoDescription ?? ''}
                  max={160}
                  placeholder={`${previewFleet.name} — book deliveries online and track every job live.`}
                  onChange={(v) => editPage(selected.id, { seoDescription: v || null })}
                />
              </Field>
            </section>
          ) : null}
        </div>

        {/* ---- preview column ---- */}
        <div
          className={cn(
            'space-y-4 lg:sticky lg:top-20',
            mobilePreview ? '' : 'hidden lg:block',
          )}
        >
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-breaker-ink">Live preview</h2>
            <div className="flex gap-1">
              {(['phone', 'desktop'] as const).map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setFrame(f)}
                  className={cn(
                    'rounded-md px-2.5 py-1 text-xs font-medium',
                    frame === f
                      ? 'bg-breaker-ink text-white'
                      : 'bg-white border border-breaker-line-2 text-breaker-ink-2',
                  )}
                >
                  {f === 'phone' ? 'Phone' : 'Desktop'}
                </button>
              ))}
            </div>
          </div>
          <SitePagePreview
            fleetName={previewFleet.name}
            logoUrl={brand.logoUrl}
            primaryHex={brand.primaryHex}
            accentHex={brand.accentHex}
            sections={previewSections}
            frame={frame}
            nav={previewNav}
          />
          <div className="flex flex-wrap items-start gap-4">
            <TrackingCardPreview
              primaryHex={brand.primaryHex}
              accentHex={brand.accentHex}
              fleetName={previewFleet.name}
            />
            <DriverAppPreview
              primaryHex={brand.primaryHex}
              accentHex={brand.accentHex}
              fleetName={previewFleet.name}
              logoUrl={brand.logoUrl}
            />
          </div>
          <p className="text-2xs text-breaker-mute">
            Your colours follow through to customer tracking and the driver app.
          </p>
        </div>
      </div>

      {/* ---- add-section picker ---- */}
      {pickerOpen ? (
        <div
          className="fixed inset-0 z-40 flex items-end sm:items-center justify-center bg-breaker-ink/30 p-4"
          onClick={() => setPickerOpen(false)}
        >
          <div
            className="w-full max-w-lg rounded-lg bg-white p-4 shadow-xl max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="mb-3 text-sm font-semibold text-breaker-ink">
              What do you want to add?
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {SECTION_TYPES.map((t) => (
                <Button
                  key={t.type}
                  type="button"
                  variant="ghost"
                  action={() => onAdd(t.type)}
                  className="block h-auto whitespace-normal rounded-md border border-breaker-line p-3 text-left font-normal hover:bg-breaker-ground-2"
                >
                  <SectionThumb type={t.type} />
                  <p className="mt-2 text-sm font-medium text-breaker-ink">{t.name}</p>
                  <p className="text-xs text-breaker-mute">{t.blurb}</p>
                </Button>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      <p className="mt-8 text-xs text-breaker-mute">
        Looking for your web address or the on/off switch? They stay in{' '}
        <Link to="/settings" className="underline underline-offset-2">
          Settings
        </Link>
        .
      </p>
    </div>
  );
}

/** Tiny wireframe thumbnails — visual, no words needed. */
function SectionThumb({ type }: { type: PortalSectionType }) {
  const bar = 'rounded-sm bg-breaker-line';
  return (
    <div className="flex h-12 w-full flex-col items-center justify-center gap-1 rounded bg-breaker-ground-2 p-2">
      {type === 'hero' ? (
        <>
          <div className={cn(bar, 'h-2 w-2/3')} />
          <div className={cn(bar, 'h-1.5 w-1/2')} />
          <div className="h-2 w-10 rounded-sm bg-breaker-ink/40" />
        </>
      ) : type === 'about' ? (
        <div className="flex w-full gap-2">
          <div className="flex-1 space-y-1">
            <div className={cn(bar, 'h-1.5 w-full')} />
            <div className={cn(bar, 'h-1.5 w-5/6')} />
            <div className={cn(bar, 'h-1.5 w-4/6')} />
          </div>
          <div className={cn(bar, 'h-8 w-8')} />
        </div>
      ) : type === 'highlights' ? (
        <div className="flex gap-1.5">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-3 w-10 rounded-full bg-breaker-line" />
          ))}
        </div>
      ) : type === 'gallery' ? (
        <div className="grid w-full grid-cols-4 gap-1">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className={cn(bar, 'aspect-[4/3]')} />
          ))}
        </div>
      ) : type === 'faq' ? (
        <div className="w-full space-y-1">
          {[0, 1].map((i) => (
            <div key={i} className="flex items-center gap-1">
              <div className="h-1.5 w-1.5 rounded-full bg-breaker-ink/40" />
              <div className={cn(bar, 'h-1.5 flex-1')} />
            </div>
          ))}
        </div>
      ) : type === 'coverage' ? (
        <div className="flex flex-wrap justify-center gap-1">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-2.5 w-8 rounded-full border border-breaker-line" />
          ))}
        </div>
      ) : type === 'cta' ? (
        <div className="flex w-full flex-col items-center gap-1 rounded bg-breaker-ink/15 py-1.5">
          <div className={cn(bar, 'h-1.5 w-1/2')} />
          <div className="h-2 w-10 rounded-sm bg-breaker-ink/40" />
        </div>
      ) : (
        <div className="w-full space-y-1">
          <div className={cn(bar, 'h-1.5 w-1/2')} />
          <div className={cn(bar, 'h-1.5 w-2/3')} />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section card: drag handle + on/off + delete + per-type plain form.
// ---------------------------------------------------------------------------

function SectionCard({
  section,
  onEdit,
  onToggle,
  onDelete,
  uploadImage,
}: {
  section: LocalSection;
  onEdit: (content: Record<string, unknown>) => void;
  onToggle: (enabled: boolean) => void;
  onDelete: () => Promise<unknown>;
  uploadImage: (kind: 'hero' | 'about' | 'gallery', file: File) => Promise<string | null>;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: section.id });
  const meta = SECTION_TYPES.find((t) => t.type === section.type);
  const [open, setOpen] = useState(true);

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        'surface rounded-lg',
        isDragging ? 'opacity-70 shadow-lg' : '',
        !section.enabled ? 'opacity-60' : '',
      )}
    >
      <div className="flex items-center gap-2 border-b border-breaker-line px-3 py-2">
        <button
          type="button"
          aria-label="Drag to reorder"
          className="cursor-grab touch-none rounded p-2 text-breaker-mute hover:bg-breaker-ground-2"
          {...attributes}
          {...listeners}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor" aria-hidden>
            <circle cx="4" cy="3" r="1.3" />
            <circle cx="10" cy="3" r="1.3" />
            <circle cx="4" cy="7" r="1.3" />
            <circle cx="10" cy="7" r="1.3" />
            <circle cx="4" cy="11" r="1.3" />
            <circle cx="10" cy="11" r="1.3" />
          </svg>
        </button>
        <button
          type="button"
          className="flex-1 text-left text-sm font-medium text-breaker-ink"
          onClick={() => setOpen((v) => !v)}
        >
          {meta?.name ?? section.type}
        </button>
        <label className="flex items-center gap-1.5 text-2xs text-breaker-mute">
          Show
          <input
            type="checkbox"
            checked={section.enabled}
            onChange={(e) => onToggle(e.target.checked)}
          />
        </label>
        <Button variant="ghost" size="sm" action={onDelete}>
          Remove
        </Button>
      </div>
      {open ? (
        <div className="p-3">
          <SectionForm section={section} onEdit={onEdit} uploadImage={uploadImage} />
        </div>
      ) : null}
    </div>
  );
}

function ImageField({
  label,
  kind,
  value,
  onChange,
  uploadImage,
}: {
  label: string;
  kind: 'hero' | 'about' | 'gallery';
  value: string | null;
  onChange: (url: string | null) => void;
  uploadImage: (kind: 'hero' | 'about' | 'gallery', file: File) => Promise<string | null>;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  return (
    <div className="flex items-center gap-3">
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (!file) return;
          setBusy(true);
          void uploadImage(kind, file).then((url) => {
            setBusy(false);
            if (url) onChange(url);
            if (inputRef.current) inputRef.current.value = '';
          });
        }}
      />
      {value ? (
        <img src={value} alt="" className="h-12 w-16 rounded object-cover" />
      ) : null}
      <Button
        variant="secondary"
        size="sm"
        loading={busy}
        onClick={() => inputRef.current?.click()}
      >
        {value ? `Change ${label}` : `Add ${label}`}
      </Button>
      {value ? (
        <button
          type="button"
          className="text-2xs text-breaker-mute underline underline-offset-2"
          onClick={() => onChange(null)}
        >
          Remove
        </button>
      ) : null}
    </div>
  );
}

function SectionForm({
  section,
  onEdit,
  uploadImage,
}: {
  section: LocalSection;
  onEdit: (content: Record<string, unknown>) => void;
  uploadImage: (kind: 'hero' | 'about' | 'gallery', file: File) => Promise<string | null>;
}) {
  const c = section.content;
  const set = (patch: Record<string, unknown>) => onEdit({ ...c, ...patch });

  switch (section.type) {
    case 'hero':
      return (
        <div className="space-y-3">
          <Field label="Big headline" htmlFor={`${section.id}-headline`}>
            <CountedInput
              id={`${section.id}-headline`}
              value={String(c['headline'] ?? '')}
              max={60}
              placeholder="e.g. Deliveries across the North East, same day"
              onChange={(v) => set({ headline: v || undefined })}
            />
          </Field>
          <Field label="Line underneath" htmlFor={`${section.id}-subline`}>
            <CountedInput
              id={`${section.id}-subline`}
              value={String(c['subline'] ?? '')}
              max={120}
              placeholder="e.g. Book in two minutes and follow your delivery live."
              onChange={(v) => set({ subline: v || undefined })}
            />
          </Field>
          <ImageField
            label="banner photo"
            kind="hero"
            value={(c['imageUrl'] as string) ?? null}
            onChange={(url) => set({ imageUrl: url ?? undefined })}
            uploadImage={uploadImage}
          />
        </div>
      );
    case 'about':
      return (
        <div className="space-y-3">
          <Field label="Heading" htmlFor={`${section.id}-heading`}>
            <CountedInput
              id={`${section.id}-heading`}
              value={String(c['heading'] ?? '')}
              max={60}
              placeholder="e.g. About us"
              onChange={(v) => set({ heading: v || undefined })}
            />
          </Field>
          <Field label="What do you want to say?" htmlFor={`${section.id}-body`}>
            <CountedInput
              id={`${section.id}-body`}
              textarea
              value={String(c['body'] ?? '')}
              max={600}
              placeholder="e.g. We've moved freight for Tyneside businesses for 12 years — same faces, same vans, no surprises."
              onChange={(v) => set({ body: v || undefined })}
            />
          </Field>
          <ImageField
            label="photo"
            kind="about"
            value={(c['photoUrl'] as string) ?? null}
            onChange={(url) => set({ photoUrl: url ?? undefined })}
            uploadImage={uploadImage}
          />
        </div>
      );
    case 'highlights': {
      const items =
        (c['items'] as Array<{ kind: 'preset' | 'custom'; value: string }>) ?? [];
      const setItems = (next: typeof items) => set({ items: next });
      return (
        <div className="space-y-2">
          {items.map((item, i) => (
            <div key={i} className="flex items-center gap-2">
              <select
                value={item.kind === 'preset' ? item.value : '__custom'}
                onChange={(e) => {
                  const v = e.target.value;
                  const next = [...items];
                  next[i] =
                    v === '__custom'
                      ? { kind: 'custom', value: item.kind === 'custom' ? item.value : '' }
                      : { kind: 'preset', value: v };
                  setItems(next);
                }}
                className="h-9 rounded-md border border-breaker-line-2 bg-white px-2 text-sm"
              >
                {HIGHLIGHT_PRESETS.map((p) => (
                  <option key={p} value={p}>
                    {HIGHLIGHT_LABELS[p]}
                  </option>
                ))}
                <option value="__custom">Write my own…</option>
              </select>
              {item.kind === 'custom' ? (
                <Input
                  value={item.value}
                  maxLength={40}
                  placeholder="e.g. Free saturday collections"
                  onChange={(e) => {
                    const next = [...items];
                    next[i] = { kind: 'custom', value: e.target.value };
                    setItems(next);
                  }}
                />
              ) : null}
              {items.length > 3 ? (
                <button
                  type="button"
                  className="text-2xs text-breaker-mute underline"
                  onClick={() => setItems(items.filter((_, j) => j !== i))}
                >
                  Remove
                </button>
              ) : null}
            </div>
          ))}
          {items.length < 6 ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setItems([...items, { kind: 'preset', value: 'nationwide' }])}
            >
              Add another
            </Button>
          ) : null}
        </div>
      );
    }
    case 'gallery': {
      const images = (c['images'] as Array<{ url: string; caption?: string }>) ?? [];
      const setImages = (next: typeof images) => set({ images: next });
      return (
        <div className="space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {images.map((img, i) => (
              <div key={i} className="space-y-1">
                <img src={img.url} alt="" className="aspect-[4/3] w-full rounded object-cover" />
                <Input
                  value={img.caption ?? ''}
                  maxLength={80}
                  placeholder="Caption (optional)"
                  onChange={(e) => {
                    const next = [...images];
                    const caption = e.target.value;
                    next[i] = caption ? { url: img.url, caption } : { url: img.url };
                    setImages(next);
                  }}
                />
                <button
                  type="button"
                  className="text-2xs text-breaker-mute underline"
                  onClick={() => setImages(images.filter((_, j) => j !== i))}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
          {images.length < 8 ? (
            <ImageField
              label="photo"
              kind="gallery"
              value={null}
              onChange={(url) => {
                if (url) setImages([...images, { url }]);
              }}
              uploadImage={uploadImage}
            />
          ) : (
            <p className="text-2xs text-breaker-mute">Eight photos is the maximum.</p>
          )}
        </div>
      );
    }
    case 'faq': {
      const items = (c['items'] as Array<{ q: string; a: string }>) ?? [];
      const setItems = (next: typeof items) => set({ items: next });
      return (
        <div className="space-y-3">
          {items.map((item, i) => (
            <div key={i} className="rounded-md border border-breaker-line p-2 space-y-2">
              <CountedInput
                id={`${section.id}-q${i}`}
                value={item.q}
                max={80}
                placeholder="e.g. How fast can you collect?"
                onChange={(v) => {
                  const next = [...items];
                  next[i] = { q: v, a: item.a };
                  setItems(next);
                }}
              />
              <CountedInput
                id={`${section.id}-a${i}`}
                textarea
                value={item.a}
                max={400}
                placeholder="e.g. Usually within the hour for local jobs — call us for a same-day slot."
                onChange={(v) => {
                  const next = [...items];
                  next[i] = { q: item.q, a: v };
                  setItems(next);
                }}
              />
              <button
                type="button"
                className="text-2xs text-breaker-mute underline"
                onClick={() => setItems(items.filter((_, j) => j !== i))}
              >
                Remove this question
              </button>
            </div>
          ))}
          {items.length < 8 ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setItems([...items, { q: '', a: '' }])}
            >
              Add a question
            </Button>
          ) : (
            <p className="text-2xs text-breaker-mute">Eight questions is the maximum.</p>
          )}
        </div>
      );
    }
    case 'coverage': {
      const areas = (c['areas'] as string[]) ?? [];
      const setAreas = (next: string[]) => set({ areas: next });
      return (
        <div className="space-y-2">
          <p className="text-xs text-breaker-mute">
            The places you deliver — shown as a list of badges. Leave empty to
            show &quot;Nationwide&quot;.
          </p>
          {areas.map((area, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input
                value={area}
                maxLength={40}
                placeholder="e.g. Newcastle upon Tyne"
                onChange={(e) => {
                  const next = [...areas];
                  next[i] = e.target.value;
                  setAreas(next);
                }}
              />
              <button
                type="button"
                className="text-2xs text-breaker-mute underline"
                onClick={() => setAreas(areas.filter((_, j) => j !== i))}
              >
                Remove
              </button>
            </div>
          ))}
          {areas.length < 12 ? (
            <Button variant="ghost" size="sm" onClick={() => setAreas([...areas, ''])}>
              Add an area
            </Button>
          ) : (
            <p className="text-2xs text-breaker-mute">Twelve areas is the maximum.</p>
          )}
        </div>
      );
    }
    case 'text':
      return (
        <RichTextEditor doc={c['doc'] ?? EMPTY_DOC} onChange={(doc) => set({ doc })} />
      );
    case 'cta':
      return (
        <div className="space-y-2">
          <Field label="Banner line" htmlFor={`${section.id}-line`}>
            <CountedInput
              id={`${section.id}-line`}
              value={String(c['line'] ?? '')}
              max={80}
              placeholder="e.g. Ready to move something?"
              onChange={(v) => set({ line: v || undefined })}
            />
          </Field>
          <p className="text-2xs text-breaker-mute">
            The &quot;Book a delivery&quot; button is added for you.
          </p>
        </div>
      );
    case 'contact': {
      const overrides = (c['overrides'] as Record<string, string>) ?? {};
      const setOverride = (key: string, v: string) =>
        set({
          overrides: { ...overrides, [key]: v || undefined },
        });
      return (
        <div className="space-y-3">
          <p className="text-xs text-breaker-mute">
            Filled in from your fleet details automatically — only type here to
            show something different on the website.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Phone" htmlFor={`${section.id}-phone`}>
              <Input
                id={`${section.id}-phone`}
                value={overrides['phone'] ?? ''}
                maxLength={20}
                placeholder="Uses your fleet phone"
                onChange={(e) => setOverride('phone', e.target.value)}
              />
            </Field>
            <Field label="Email" htmlFor={`${section.id}-email`}>
              <Input
                id={`${section.id}-email`}
                value={overrides['email'] ?? ''}
                maxLength={120}
                placeholder="Uses your fleet email"
                onChange={(e) => setOverride('email', e.target.value)}
              />
            </Field>
            <Field label="Address" htmlFor={`${section.id}-address`}>
              <Input
                id={`${section.id}-address`}
                value={overrides['address'] ?? ''}
                maxLength={160}
                placeholder="Uses your fleet address"
                onChange={(e) => setOverride('address', e.target.value)}
              />
            </Field>
            <Field label="Opening hours" htmlFor={`${section.id}-hours`}>
              <Input
                id={`${section.id}-hours`}
                value={overrides['openingHours'] ?? ''}
                maxLength={160}
                placeholder="e.g. Mon–Fri 7am–6pm"
                onChange={(e) => setOverride('openingHours', e.target.value)}
              />
            </Field>
          </div>
        </div>
      );
    }
    default:
      return (
        <p className="text-xs text-breaker-mute">
          This section type gets its editor in an upcoming update — it still
          shows on your site.
        </p>
      );
  }
}
